import React, { useState, useEffect } from 'react';
import { X, CheckCircle, Loader, User } from 'lucide-react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import type { BulkOperatorOption } from './BulkUpdateModal';

interface ApprovePListsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Antall plukklister som er valgt. */
  selectedCount: number;
  /** Godkjenning pågår (parent eier flyten). */
  isApproving: boolean;
  /** Operatører for valgfri tildeling. Tom liste skjuler nedtrekket. */
  operators: BulkOperatorOption[];
  /** Kalles ved «Godkjenn». operatorID = null → ingen tildeling. */
  onConfirm: (operatorID: string | null) => void;
}

/**
 * ApprovePListsModal — erstatter window.confirm ved «Godkjenn» i Nye plukklister (TW 36397705).
 * Lar lagersjef samtidig tildele de godkjente plukkene til en operatør (PList.OperatorID, som
 * bizClicks «Tildel til operatør»). Standard er ingen tildeling — da fungerer godkjenning som før.
 */
export const ApprovePListsModal: React.FC<ApprovePListsModalProps> = ({
  isOpen,
  onClose,
  selectedCount,
  isApproving,
  operators,
  onConfirm
}) => {
  const { t } = useTranslation();
  const [operatorID, setOperatorID] = useState('');

  useEffect(() => {
    if (isOpen) setOperatorID('');
  }, [isOpen]);

  if (!isOpen) return null;

  const selectedLabel = selectedCount === 1
    ? t('manager.modules.newPickingLists.bulkUpdate.listSingular', {}, 'plukkliste')
    : t('manager.modules.newPickingLists.bulkUpdate.listPlural', {}, 'plukklister');

  const handleConfirm = () => {
    if (isApproving) return;
    onConfirm(operatorID === '' ? null : operatorID);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" onClick={isApproving ? undefined : onClose} />

      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-green-600 to-emerald-600 rounded-t-xl">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-white" />
              <div>
                <h2 className="text-lg font-semibold text-white">
                  {t('manager.modules.newPickingLists.approval.modalTitle', {}, 'Godkjenn plukklister')}
                </h2>
                <p className="text-sm text-green-100">
                  {t('manager.modules.newPickingLists.bulkUpdate.selectedSummary', { '0': selectedCount, '1': selectedLabel }, `${selectedCount} ${selectedLabel} valgt`)}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={isApproving}
              className="text-white/80 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4 overflow-y-auto">
            <p className="text-sm text-gray-700">
              {t('manager.modules.newPickingLists.approval.modalBody', {}, 'Plukklistene går til status 10 (Godkjent) og blir tilgjengelige i operatørenes plukkmodul.')}
            </p>

            {operators.length > 0 && (
              <div className="border border-gray-200 rounded-lg p-4 bg-white">
                <label htmlFor="approve-operator-select" className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <User className="w-4 h-4 text-gray-500" />
                  {t('manager.modules.newPickingLists.bulkUpdate.operatorLabel', {}, 'Tildel til operatør')}
                </label>
                <select
                  id="approve-operator-select"
                  value={operatorID}
                  onChange={(e) => setOperatorID(e.target.value)}
                  disabled={isApproving}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white disabled:bg-gray-100"
                >
                  <option value="">{t('manager.modules.newPickingLists.approval.operatorNone', {}, '-- Ingen tildeling --')}</option>
                  {operators.map(op => (
                    <option key={op.userID} value={op.userID}>
                      {op.profileName || op.userID}
                    </option>
                  ))}
                </select>
                <p className="mt-2 text-xs text-gray-500">
                  {t('manager.modules.newPickingLists.bulkUpdate.operatorHint', {}, 'Plukkene vises bare for denne operatøren, som selv starter dem. Plukket låses ikke.')}
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-xl flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              disabled={isApproving}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.buttons.cancel', {}, 'Avbryt')}
            </button>
            <button
              onClick={handleConfirm}
              disabled={isApproving}
              className="flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-lg transition-colors bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              {isApproving ? <Loader className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              {isApproving
                ? t('manager.modules.newPickingLists.approval.approving', {}, 'Godkjenner...')
                : t('manager.modules.newPickingLists.approval.confirmButton', {}, 'Godkjenn')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApprovePListsModal;
